<template>
    <div>
        <h1 class="card-title h5">{{ $t('common.dear_guest') }}</h1>

        <!-- Personal data required first -->
        <div v-if="voucher.personalDataRequired">

            <transition name="fade">
                <ContactForm v-if="!isPersonalInfoFormSubmitted"
                             :is-sending="isSendingData"
                             :show-newsletter-checkbox="showNewsletterCheckbox"
                             :sendVoucher="true"
                             @submit="savePersonalData">
                    <template v-slot:before>
                        <p class="card-text">{{ $t('externalBooking.personalDataRequired.intro') }}</p>
                    </template>

                    <template v-slot:after>
                        <GdprNotice/>
                    </template>
                </ContactForm>
            </transition>

            <CustomerServiceBlock/>
        </div>

        <div v-else-if="activationData.bookingServiceUrl || activationData.bookingServiceEmail">
            <!-- external bookingservice via url -->
            <template v-if="activationData.bookingServiceUrl">
                <i18n path="externalBooking.urlBooking.intro" tag="p" class="card-text">
                    <span slot="booking_service_name">{{ activationData.bookingServiceName }}</span>
                    <mark slot="hotelname">{{ activationData.arrangement.hotelname }}</mark>
                    <span slot="bookingLink">
                        <a :href="activationData.bookingServiceUrl" class="btn-link" target="_blank"
                           rel="nofollow">
                            {{
                                $t('externalBooking.urlBooking.goToBookingPage')
                            }}
                        </a>
                    </span>
                </i18n>

                <i18n v-if="activationData.externalVoucherDownload"
                      path="externalBooking.urlBooking.externalDownloadText" tag="p"
                      class="card-text"
                >
                    <span slot="booking_service_name">{{ activationData.bookingServiceName }}</span>
                </i18n>

                <i18n v-else-if="activationData.externalVoucherCode"
                      path="externalBooking.urlBooking.externalCodeText" tag="p"
                      class="card-text"
                >
                    <span slot="booking_service_name">{{ activationData.bookingServiceName }}</span>
                    <mark slot="external_voucher_code">{{ activationData.externalVoucherCode }}</mark>
                </i18n>

                <p class="card-text">
                    {{ $t('externalBooking.urlBooking.externalDurationText') }}
                </p>
            </template>

            <!-- external bookingservice via email -->
            <template v-if="activationData.bookingServiceEmail">
                <i18n v-if="activationData.externalVoucherCode"
                      path="externalBooking.emailBooking.externalCodeText" tag="p"
                      class="card-text">
                    <span slot="hotelname">{{ activationData.arrangement.hotelname }}</span>
                    <mark slot="external_voucher_code">{{ activationData.externalVoucherCode }}</mark>
                    <a slot="booking_service_email" :href="bookingServiceEmailLink" target="_blank"
                       style="text-decoration:underline;" rel="nofollow">{{ activationData.bookingServiceEmail }}
                    </a>
                </i18n>

                <i18n v-else path="externalBooking.supporting_text.external_email.animod_voucher_code.text" tag="p"
                      class="card-text">
                    <span slot="hotelname">{{ activationData.arrangement.hotelname }}</span>
                    <a slot="booking_service_email" :href="bookingServiceEmailLink" target="_blank"
                       style="text-decoration:underline;" rel="nofollow">{{ activationData.bookingServiceEmail }}
                    </a>
                </i18n>
            </template>

            <transition name="fade">
                <ContactForm v-if="showVoucherInfoForm"
                             :submit-text="$t('sendVoucherInfo.action.generic.submit.text')"
                             :is-sending="isSendingData"
                             :sendVoucher="true"
                             :showNewsletterCheckbox="showNewsletterCheckbox"
                             @submit="sendVoucherInfo">
                    <template v-slot:before>
                        <p class="pt-2 card-text">
                            {{ $t('sendVoucherInfo.supporting_text.common.form_intro') }}
                        </p>
                    </template>

                    <template v-slot:after>
                        <GdprNotice/>
                    </template>
                </ContactForm>
            </transition>

            <p class="card-text">
                {{ $t('externalBooking.supporting_text.common.outro.text') }}
            </p>

            <div class="card-navigation justify-content-center justify-content-md-between align-items-start flex-wrap">
                <button type="button" class="btn btn-secondary mb-3 mb-md-0" @click="$store.dispatch('reset')">
                    {{ $t('common.redeem_another_voucher') }}
                </button>

                <a v-if="activationData.bookingServiceUrl" :href="activationData.bookingServiceUrl" class="btn btn-primary"
                   target="_blank" rel="nofollow">
                    {{$t('externalBooking.urlBooking.goToBookingPage')}}
                </a>
            </div>
        </div>

        <div v-else>
            <!--
            This case should actually never happen but just in case external booking is used and neither url nor email
            are provided we show a default message referring to the Animod customer support.
            -->
            <i18n path="externalBooking.errors.generic.text" tag="p" class="card-text">
                <span slot="hotelname">{{ activationData.arrangement.hotelname }}</span>
                <span slot="support_mail"><CustomerServiceEmail/></span>
                <span slot="support_phone"><CustomerServicePhone/></span>
            </i18n>
            <div class="card-navigation">
                <button type="button" class="btn btn-secondary" @click="$store.dispatch('reset')">
                    {{ $t('common.redeem_another_voucher') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import CustomerServiceBlock from './CustomerServiceBlock'
import CustomerServiceEmail from './CustomerServiceEmail'
import CustomerServicePhone from './CustomerServicePhone'
import ErrorMessages from '../components/ErrorMessages'
import GdprNotice from '../components/GdprNotice'
import ContactForm from '@/components/ContactForm'
import { mapState, mapGetters } from 'vuex'

export default {
    name: 'ExternalBookingServiceForm',
    components: {
        CustomerServiceEmail,
        CustomerServiceBlock,
        CustomerServicePhone,
        ErrorMessages,
        GdprNotice,
        ContactForm
    },
    data () {
        return {
            errors: [],
            isVoucherInfoFormSubmitted: false,
            isPersonalInfoFormSubmitted: false,
            isSendingData: false,
            firstname: '',
            lastname: '',
            email: '',
            placeholder_firstname: this.$i18n.t('generic.firstname'),
            placeholder_lastname: this.$i18n.t('generic.lastname'),
            placeholder_email: this.$i18n.t('generic.email'),
            showNewsletterCheckbox: true,
        }
    },
    computed: {
        ...mapState(['voucher', 'activationData']),
        ...mapGetters(['voucherCode']),
        showVoucherInfoForm () {
            return (this.activationData.externalVoucherCode || this.activationData.externalVoucherDownload) && !this.isVoucherInfoFormSubmitted
        },
        bookingServiceEmailLink () {
            return 'mailto:' + this.activationData.bookingServiceEmail
        },
        isExternalVoucherDownload() {
            return this.activationData.externalVoucherDownload
        }
    },
    methods: {
        async sendVoucherInfo (contactData) {
            try {
                this.isSendingData = true
                const { successful } = await this.$store.dispatch('sendVoucherDataRequest', {
                    ...contactData,
                    voucherCode: this.voucherCode
                })
                this.isVoucherInfoFormSubmitted = successful
            } finally {
                this.isSendingData = false
            }
        },
        async savePersonalData (contactData) {
            try {
                this.isSendingData = true
                const response = await this.$store.dispatch('savePersonalData', {
                    ...contactData,
                    voucherCode: this.voucherCode
                })
                this.isPersonalInfoFormSubmitted = true
            } finally {
                this.isSendingData = false
            }
        }
    }
}
</script>
<style lang="scss" scoped>
@use "@/styles/_variables";
@use "sass:map";

.avp-text-highlight {
    background-color: rgba(#fff, 1);
    color: map.get(variables.$colors, 'secondary');
}

mark {
    display: block;
    background: transparent;
    color: hsl(0, 0%, 0%);
    font-size: 18px;
    padding: 0 0.3125rem;
    text-align: center;
    font-family: "Courier New", monospace, serif;
    font-weight: bold;
}

.avp-form-button-row {
    padding: 0 5px;
    justify-content: center;

    button {
        margin: 0 .375rem;
    }

    @media (max-width: 575px) {
        flex-wrap: wrap-reverse;
    }
}
</style>
